import * as Y from "yjs";

const DEFAULT_LANE_HEIGHT = 90;

function getRoot(doc) {
  return doc.getMap("room");
}

function ensureMap(root, key) {
  const existing = root.get(key);
  if (existing instanceof Y.Map) return existing;
  const next = new Y.Map();
  root.set(key, next);
  return next;
}

function ensureArray(root, key) {
  const existing = root.get(key);
  if (existing instanceof Y.Array) return existing;
  const next = new Y.Array();
  root.set(key, next);
  return next;
}

function ensureText(root, key) {
  const existing = root.get(key);
  if (existing instanceof Y.Text) return existing;
  const next = new Y.Text();
  root.set(key, next);
  return next;
}

function getMetaMap(doc) {
  return ensureMap(getRoot(doc), "meta");
}

function getStrokesMap(doc) {
  return ensureMap(getRoot(doc), "strokes");
}

function getStrokeOrder(doc) {
  return ensureArray(getRoot(doc), "strokeOrder");
}

function getLaneHeightsArray(doc) {
  return ensureArray(getRoot(doc), "laneHeights");
}

function getEditorText(doc) {
  return ensureText(getRoot(doc), "editorLatex");
}

function getAttemptStateMap(doc) {
  return ensureMap(getRoot(doc), "attemptState");
}

function getBreakTimeStateMap(doc) {
  return ensureMap(getRoot(doc), "breakTimeState");
}

function replaceYArray(target, values) {
  target.delete(0, target.length);
  if (Array.isArray(values) && values.length) {
    target.push(values);
  }
}

function replaceYText(target, value) {
  const nextValue = String(value || "");
  if (target.toString() === nextValue) return;
  if (target.length) target.delete(0, target.length);
  if (nextValue) target.insert(0, nextValue);
}

export function createCollaborationDoc() {
  return new Y.Doc();
}

export function buildCollaborationWsUrl(apiBase, roomToken, token) {
  const base = new URL(String(apiBase || window.location.origin));
  const protocol = base.protocol === "https:" ? "wss:" : "ws:";
  const wsUrl = new URL(`${protocol}//${base.host}/collaboration-ws`);
  wsUrl.searchParams.set("roomToken", String(roomToken || ""));
  wsUrl.searchParams.set("token", String(token || ""));
  return wsUrl.toString();
}

export function readSharedCanvasState(doc) {
  const meta = getMetaMap(doc);
  const strokesMap = getStrokesMap(doc);
  const strokeOrder = getStrokeOrder(doc).toArray().map((value) => String(value || "")).filter(Boolean);
  const strokes = strokeOrder
    .map((strokeId) => {
      const raw = strokesMap.get(strokeId);
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (err) {
        return null;
      }
    })
    .filter(Boolean);
  return {
    revision: Number(meta.get("revision") || 0),
    updatedAt: meta.get("updatedAt") || null,
    stageWidth: Number(meta.get("stageWidth") || 1200),
    stageHeight: Number(meta.get("stageHeight") || 760),
    paperStyle: String(meta.get("paperStyle") || "white"),
    laneHeights: getLaneHeightsArray(doc).toArray().map(Number).filter(Number.isFinite),
    strokes
  };
}

export function readSharedEditorLatex(doc) {
  return getEditorText(doc).toString();
}

export function readSharedAttemptState(doc) {
  const attemptState = getAttemptStateMap(doc);
  const raw = attemptState.get("json");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
}

export function readSharedBreakTimeState(doc) {
  const breakTimeState = getBreakTimeStateMap(doc);
  const raw = breakTimeState.get("json");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
}

function nextRevision(meta, delta = 1) {
  return Math.max(0, Number(meta.get("revision") || 0)) + Math.max(1, Number(delta) || 1);
}

function markMetaUpdate(meta, patch = {}) {
  if (Object.prototype.hasOwnProperty.call(patch, "revision")) {
    meta.set("revision", Number(patch.revision || 0));
  }
  if (Object.prototype.hasOwnProperty.call(patch, "stageWidth")) {
    meta.set("stageWidth", Number(patch.stageWidth || 1200));
  }
  if (Object.prototype.hasOwnProperty.call(patch, "stageHeight")) {
    meta.set("stageHeight", Number(patch.stageHeight || 760));
  }
  meta.set("updatedAt", new Date().toISOString());
}

export function appendCommittedStrokes(doc, strokes, metaPatch = {}) {
  const cleanStrokes = Array.isArray(strokes) ? strokes.filter((stroke) => stroke && stroke.strokeId) : [];
  if (!cleanStrokes.length) return;
  doc.transact(() => {
    const meta = getMetaMap(doc);
    const strokesMap = getStrokesMap(doc);
    const strokeOrder = getStrokeOrder(doc);
    const existingIds = new Set(strokeOrder.toArray().map((value) => String(value || "")));
    const appendedIds = [];
    cleanStrokes.forEach((stroke) => {
      const strokeId = String(stroke.strokeId || stroke.id || "").trim();
      if (!strokeId || existingIds.has(strokeId)) return;
      existingIds.add(strokeId);
      strokesMap.set(strokeId, JSON.stringify(stroke));
      appendedIds.push(strokeId);
    });
    if (appendedIds.length) {
      strokeOrder.push(appendedIds);
      markMetaUpdate(meta, {
        revision: nextRevision(meta, appendedIds.length),
        stageWidth: metaPatch.stageWidth,
        stageHeight: metaPatch.stageHeight
      });
    }
  }, "local");
}

export function removeCommittedStroke(doc, strokeId, metaPatch = {}) {
  const normalizedStrokeId = String(strokeId || "").trim();
  if (!normalizedStrokeId) return;
  doc.transact(() => {
    const meta = getMetaMap(doc);
    const strokesMap = getStrokesMap(doc);
    const strokeOrder = getStrokeOrder(doc);
    const currentOrder = strokeOrder.toArray().map((value) => String(value || ""));
    const index = currentOrder.findIndex((value) => value === normalizedStrokeId);
    const hadStroke = strokesMap.has(normalizedStrokeId) || index !== -1;
    if (!hadStroke) return;
    if (index !== -1) strokeOrder.delete(index, 1);
    strokesMap.delete(normalizedStrokeId);
    markMetaUpdate(meta, {
      revision: nextRevision(meta, 1),
      stageWidth: metaPatch.stageWidth,
      stageHeight: metaPatch.stageHeight
    });
  }, "local");
}

export function replaceLaneHeights(doc, laneHeights, metaPatch = {}) {
  const heights = Array.isArray(laneHeights) && laneHeights.length
    ? laneHeights.map(Number).filter(Number.isFinite)
    : Array.from({ length: 10 }, () => DEFAULT_LANE_HEIGHT);
  doc.transact(() => {
    const meta = getMetaMap(doc);
    replaceYArray(getLaneHeightsArray(doc), heights);
    markMetaUpdate(meta, {
      revision: nextRevision(meta, 1),
      stageWidth: metaPatch.stageWidth,
      stageHeight: metaPatch.stageHeight
    });
  }, "local");
}

export function clearSharedCanvas(doc, laneHeights, metaPatch = {}, syncPatch = {}) {
  const heights = Array.isArray(laneHeights) && laneHeights.length
    ? laneHeights.map(Number).filter(Number.isFinite)
    : Array.from({ length: 10 }, () => DEFAULT_LANE_HEIGHT);
  const shouldReplaceEditor = Object.prototype.hasOwnProperty.call(syncPatch || {}, "editorLatex");
  const nextAttemptState =
    syncPatch?.attemptState && typeof syncPatch.attemptState === "object"
      ? syncPatch.attemptState
      : null;
  doc.transact(() => {
    const meta = getMetaMap(doc);
    const strokesMap = getStrokesMap(doc);
    const strokeOrder = getStrokeOrder(doc);
    const keys = Array.from(strokesMap.keys());
    keys.forEach((key) => strokesMap.delete(key));
    strokeOrder.delete(0, strokeOrder.length);
    replaceYArray(getLaneHeightsArray(doc), heights);
    if (shouldReplaceEditor) {
      replaceYText(getEditorText(doc), syncPatch.editorLatex || "");
    }
    if (nextAttemptState) {
      const attemptState = getAttemptStateMap(doc);
      attemptState.set("json", JSON.stringify(nextAttemptState));
      attemptState.set("updatedAt", String(nextAttemptState.updatedAt || new Date().toISOString()));
    }
    markMetaUpdate(meta, {
      revision: nextRevision(meta, 1),
      stageWidth: metaPatch.stageWidth,
      stageHeight: metaPatch.stageHeight
    });
  }, "local");
}

export function setSharedEditorLatex(doc, value) {
  doc.transact(() => {
    replaceYText(getEditorText(doc), value || "");
  }, "local");
}

export function setSharedAttemptState(doc, attemptLike) {
  if (!attemptLike || typeof attemptLike !== "object") return;
  doc.transact(() => {
    const attemptState = getAttemptStateMap(doc);
    attemptState.set("json", JSON.stringify(attemptLike));
    attemptState.set("updatedAt", String(attemptLike.updatedAt || new Date().toISOString()));
  }, "local");
}

export function setSharedPaperStyle(doc, paperStyle) {
  if (!doc || !paperStyle) return;
  doc.transact(() => {
    const meta = getMetaMap(doc);
    meta.set("paperStyle", String(paperStyle));
    meta.set("updatedAt", new Date().toISOString());
  }, "local");
}

export function setSharedBreakTimeState(doc, breakTimeLike) {
  if (!breakTimeLike || typeof breakTimeLike !== "object") return;
  doc.transact(() => {
    const breakTimeState = getBreakTimeStateMap(doc);
    breakTimeState.set("json", JSON.stringify(breakTimeLike));
    breakTimeState.set("updatedAt", String(breakTimeLike.updatedAt || new Date().toISOString()));
  }, "local");
}
